(() => {
  if (window.__mlCompactRatingsStarted) return;
  window.__mlCompactRatingsStarted = true;

  const nativeSelector = '.ml-rating-summary[data-ml-rating][data-ml-rating-count]';
  const badgeSelector = '.jdgm-prev-badge[data-average-rating][data-number-of-reviews]';
  const locale = document.documentElement.lang || 'de-DE';
  const isGerman = locale.toLowerCase().startsWith('de');
  const reviewLabel = isGerman ? 'Bewertungen' : 'reviews';
  const ratingFormatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });
  const countFormatter = new Intl.NumberFormat(locale);
  const relevantSelector = `${nativeSelector}, ${badgeSelector}, .jdgm-widget`;

  const collect = (root, selector) => {
    const matches = [];

    if (root?.nodeType === Node.ELEMENT_NODE && root.matches(selector)) {
      matches.push(root);
    }

    root?.querySelectorAll?.(selector).forEach((element) => matches.push(element));
    return matches;
  };

  const compactNativeRatings = (root) => {
    collect(root, nativeSelector).forEach((summary) => {
      const average = Number.parseFloat(summary.dataset.mlRating);
      const count = Number.parseInt(summary.dataset.mlRatingCount, 10);
      const score = summary.querySelector('.ml-rating-score');
      const reviews = summary.querySelector('.ml-rating-reviews');

      if (score && Number.isFinite(average)) {
        const formattedScore = ratingFormatter.format(average);
        if (score.textContent !== formattedScore) score.textContent = formattedScore;
      }

      if (reviews && Number.isFinite(count)) {
        const formattedCount = countFormatter.format(count);
        const compactCount = `(${formattedCount})`;

        if (reviews.textContent.trim() !== compactCount) reviews.textContent = compactCount;
        if (reviews.getAttribute('aria-label') !== `${formattedCount} ${reviewLabel}`) {
          reviews.setAttribute('aria-label', `${formattedCount} ${reviewLabel}`);
        }
      }
    });
  };

  const compactBadges = (root) => {
    collect(root, badgeSelector).forEach((badge) => {
      const average = Number.parseFloat(badge.dataset.averageRating);
      const count = Number.parseInt(badge.dataset.numberOfReviews, 10);
      const stars = badge.querySelector('.jdgm-prev-badge__stars');
      const reviewText = badge.querySelector('.jdgm-prev-badge__text');

      if (!stars || !reviewText || !Number.isFinite(average) || !Number.isFinite(count)) return;

      let score = badge.querySelector('.ml-jdgm-score');
      if (!score) {
        score = document.createElement('span');
        score.className = 'ml-jdgm-score';
        stars.insertAdjacentElement('afterend', score);
      }

      const formattedScore = ratingFormatter.format(average);
      const formattedCount = countFormatter.format(count);
      const isProductBadge = badge.closest('.product-information') && !badge.closest('product-card, .ml-product-card-rating, .ml-cart-item-rating');
      const compactCount = isProductBadge ? `${formattedCount} ${reviewLabel}` : `(${formattedCount})`;

      if (score.textContent !== formattedScore) score.textContent = formattedScore;
      if (reviewText.textContent.trim() !== compactCount) reviewText.textContent = compactCount;
      if (reviewText.getAttribute('aria-label') !== `${formattedCount} ${reviewLabel}`) {
        reviewText.setAttribute('aria-label', `${formattedCount} ${reviewLabel}`);
      }
      badge.classList.add('ml-jdgm-compact');
    });
  };

  const labelMap = new Map([
    ['View review image', 'Bewertungsbild anzeigen'],
    ['Pagination Navigation', 'Seitennavigation der Bewertungen'],
    ['Go to previous page', 'Zur vorherigen Seite'],
    ['Go to next page', 'Zur nächsten Seite'],
    ['Write a review', 'Bewertung schreiben'],
    ['Ask a question', 'Frage stellen'],
    ['Loading thumbnail', 'Vorschaubild wird geladen'],
    ['Scroll thumbnails right', 'Vorschaubilder nach rechts scrollen'],
    ['Scroll thumbnails left', 'Vorschaubilder nach links scrollen']
  ]);

  const textMap = new Map([
    ['Write a review', 'Bewertung schreiben'],
    ['Ask a question', 'Frage stellen'],
    ['Reviews', 'Bewertungen'],
    ['Questions', 'Fragen'],
    ['Most Recent', 'Neueste zuerst'],
    ['Highest Rating', 'Beste Bewertung'],
    ['Lowest Rating', 'Niedrigste Bewertung'],
    ['Most Helpful', 'Hilfreichste'],
    ['Thumbnail Navigation', 'Navigation der Vorschaubilder']
  ]);

  const localizeJudgeMe = (root) => {
    if (!isGerman) return;
    collect(root, '.jdgm-widget [aria-label], .jdgm-prev-badge[aria-label]').forEach((element) => {
      const currentLabel = element.getAttribute('aria-label')?.trim();
      if (!currentLabel) return;

      let translatedLabel = labelMap.get(currentLabel);
      const ratingMatch = currentLabel.match(/^([0-9]+(?:[.,][0-9]+)?) out of [0-9]+ stars$/i);
      const pageMatch = currentLabel.match(/^Go to page ([0-9]+)$/i);
      const distributionMatch = currentLabel.match(/^([0-9]+)% \(([0-9,.]+)\) reviews with ([1-5]) star rating$/i);
      const thumbnailCountMatch = currentLabel.match(/^Thumbnails for ([0-9]+) items$/i);
      const itemMatch = currentLabel.match(/^Go to item ([0-9]+)$/i);

      if (ratingMatch) translatedLabel = `${ratingMatch[1].replace('.', ',')} von 5 Sternen`;
      if (pageMatch) translatedLabel = `Zu Seite ${pageMatch[1]}`;
      if (distributionMatch) translatedLabel = `${distributionMatch[1]} % (${distributionMatch[2].replace(/,/g, '.')}) Bewertungen mit ${distributionMatch[3]} Sternen`;
      if (thumbnailCountMatch) translatedLabel = `Vorschaubilder für ${thumbnailCountMatch[1]} Elemente`;
      if (itemMatch) translatedLabel = `Zu Element ${itemMatch[1]}`;

      if (translatedLabel && currentLabel !== translatedLabel) {
        element.setAttribute('aria-label', translatedLabel);
      }
    });

    collect(root, '.jdgm-widget button, .jdgm-widget [role="button"], .jdgm-widget option, .jdgm-widget .sr-only').forEach((element) => {
      if (element.children.length > 0) return;
      const currentText = element.textContent.trim();
      const translatedText = textMap.get(currentText);

      if (translatedText && currentText !== translatedText) {
        element.textContent = translatedText;
      }
    });
  };

  const queuedRoots = new Set();
  let scheduledFrame = 0;

  const schedule = (root = document) => {
    if (root !== document) {
      if (root?.nodeType !== Node.ELEMENT_NODE || !root.isConnected) return;
      const relevantAncestor = root.closest(relevantSelector);
      if (relevantAncestor) {
        root = relevantAncestor;
      } else if (!root.querySelector(relevantSelector)) {
        return;
      }
    }

    for (const queuedRoot of queuedRoots) {
      if (queuedRoot === root || queuedRoot.contains(root)) return;
      if (root.contains(queuedRoot)) queuedRoots.delete(queuedRoot);
    }
    queuedRoots.add(root);
    if (scheduledFrame) return;

    scheduledFrame = requestAnimationFrame(() => {
      scheduledFrame = 0;
      const roots = Array.from(queuedRoots);
      queuedRoots.clear();

      roots.forEach((queuedRoot) => {
        if (queuedRoot !== document && !queuedRoot.isConnected) return;
        compactNativeRatings(queuedRoot);
        compactBadges(queuedRoot);
        localizeJudgeMe(queuedRoot);
      });
    });
  };

  const start = () => {
    schedule(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const target = mutation.target.nodeType === Node.ELEMENT_NODE
          ? mutation.target
          : mutation.target.parentElement;
        const relevantAncestor = target?.closest(relevantSelector);
        if (relevantAncestor) schedule(relevantAncestor);
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) schedule(node);
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-average-rating', 'data-number-of-reviews', 'data-ml-rating', 'data-ml-rating-count']
    });

    document.addEventListener('shopify:section:load', (event) => schedule(event.target));
    document.addEventListener('shopify:block:select', (event) => schedule(event.target));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
